"use client";

import AuthDialog from "@/components/AuthDialog/AuthDialog";
import MobileAuth from "@/components/AuthForms/MobileAuth";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/contexts/DialogContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { type Listing } from "@/services/api";
import { FORM_TYPES } from "@/utils";
import { isMobile } from "@/utils/helpers";
import { getResponsiveIconSize } from "@/utils/styleHelpers";
import Image from "next/image";
import { useEffect, useState } from "react";

interface FavoriteButtonProps {
  listing: Listing;
  className?: string;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  listing,
  className = "",
}) => {
  const { isAuthenticated } = useAuth();
  const { openDialog } = useDialog();
  const { determineIfFavorited, addFavorite, removeFavorite } = useFavorites();
  const [isLoading, setIsLoading] = useState(false);

  const isFavorited = determineIfFavorited(listing._id);

  useEffect(() => {
    const processPendingFavorite = async () => {
      const pendingFavoriteId = sessionStorage.getItem('pendingFavorite');
      if (pendingFavoriteId && isAuthenticated && pendingFavoriteId === listing._id && !isFavorited) {
        sessionStorage.removeItem('pendingFavorite');
        try {
          await addFavorite(listing);
        } catch (error) {
          console.error("Error processing pending favorite:", error);
        }
      }
    };

    processPendingFavorite();
  }, [isAuthenticated, listing, isFavorited, addFavorite]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      const isOnMobile = isMobile();
      
      const handleAuthSuccess = async () => {
        sessionStorage.setItem('pendingFavorite', listing._id);
      };

      openDialog({
        component: isOnMobile ? MobileAuth : AuthDialog,
        props: { 
          initialMode: FORM_TYPES.REGISTER,
          onSuccess: handleAuthSuccess,
        },
        options: {
          showCloseButton: true,
          closeOnOverlayClick: true,
          backgroundStyle: isOnMobile ? 'fullscreen' : 'modal',
        },
      });
      return;
    }

    setIsLoading(true);
    try {
      if (isFavorited) {
        await removeFavorite(listing._id);
      } else {
        await addFavorite(listing);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = getResponsiveIconSize();

  return (
    <button
      onClick={handleFavoriteClick}
      disabled={isLoading}
      className={`bg-white flex items-center justify-center --color-dark-grey hover:scale-110 cursor-pointer transition-all duration-200 ${isLoading ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
    >
      <Image
        src={isFavorited ? "/icons/favorite-filled.svg" : "/icons/favorite-unfilled.svg"}
        alt={isFavorited ? "Favorited heart" : "Unfavorited heart"}
        width={iconSize}
        height={iconSize}
      />
    </button>
  );
};

export default FavoriteButton;
